import React, { useState } from 'react';
import '../../css/Cart.css';
import { motion } from 'framer-motion';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import CheckIcon from '@material-ui/icons/Check';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Currency from './Currency';
import { NeutralSelect } from './../../components/Select';
import Input from '../../components/Input';
import { MenuItem } from '@material-ui/core';

function getDeliveryDates(min, max, eta, additionalWeeks) {
	if(eta) {
		eta = moment(eta).add(additionalWeeks, 'weeks');
	}
	const date = moment(eta ? Math.max(eta, min) : min);
	const dates = [];
	/* eslint-disable-next-line no-unmodified-loop-condition */
	while(date <= max) {
		dates.push(moment(date));
		date.add(1, 'week');
	}
	return dates;
}

function CartItem({ item, quantity, setQuantity }) {
	const { sku, description, productgroup, size, price, imageMedium, availability, eta = null, currency = 'EUR', discount } = item;
	const { t } = useTranslation();

	return <div className='cart-item'>
		<div className='modalBody-holder'>
			<motion.div
				className='carouselCard'
				initial={{ x: '-100%' }}
				animate={{ x: 0 }}
				transition={{ ease: 'easeOut', duration: 0.5 }}
			>
				<div className='carouselCardImage-holder' >
					<img className='carouselCardImage' src={imageMedium} />
				</div>
				<div className='carouselCard-holder'>

					<div className='carouselCard-content'>
						<div className='carouselCard-content-head'>
							<div className='carouselCard-content-head-title'>
								<span className='carouselCard-content-title'>{description}</span>
							</div>

						</div>
						<div className='carouselCard-content-body'>
							<span className='carouselCard-content-title'>{productgroup}</span>
						</div>
						<div className='carouselCard-content-body'>
							<span className='carouselCard-content-title'>{sku}</span>
						</div>
						<div className='carouselCard-content-body'>
							<span className='carouselCard-content-title'>{t('stock_interval', { postProcess: 'interval', count: availability })}</span>
						</div>
						<div className='carouselCard-content-body'>
							<span className='carouselCard-content-title'>
								<Currency
									quantity={price}
									currency={currency}
									discount={discount}
								/></span>
						</div>
						<div className='carouselCard-content-foot'>
							<span className='carouselCard-content-title'>{size}</span>
						</div>
						{eta && <div className='carouselCard-content-foot'>
							<span className='carouselCard-content-title'>{t('eta')} {eta.format && eta.format(t('etaFormat')) || eta}</span>
						</div>}
						<div className='carouselCard-content-price'>
							<Currency
								quantity={price * quantity}
								currency={currency}
								discount={discount}
							/>
						</div>
						<div className='carouselCard-content-buttons'>
							<div className='change-cart-amount-section'>
								<motion.button
									className='add-to-cart-min'
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.9 }}
									onClick={() => setQuantity(quantity - item.salesQuantity)}
								>
									<RemoveIcon />
								</motion.button>
								<span className='add-to-cart-amount'>
									{quantity}
								</span>
								<motion.button
									className='add-to-cart-plus'
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.9 }}
									onClick={() => setQuantity(Math.min(quantity + item.salesQuantity, item.availability))}
								>
									<AddIcon />
								</motion.button>
								<motion.button
									className='remove-from-cart-button'
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.9 }}
									onClick={() => setQuantity(0)}
								>
									<AddShoppingCartIcon />
								</motion.button>
							</div>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	</div>;
}

export default function Cart({ cart, setCartItem, createOrder, minDelivery, maxDelivery, additionalWeeks, friends = false }) {
	const [submitting, setSubmitting] = useState(false);
	const [deliveryDate, setDeliveryDate] = useState(cart.deliveryDate || '');
	const [name, setName] = useState(cart.name || '');
	const [email, setEmail] = useState(cart.email || '');
	const [phone, setPhone] = useState(cart.phone || '');
	const [address, setAddress] = useState(cart.address || '');
	const [postalCode, setPostalCode] = useState(cart.postalCode || '');
	const [city, setCity] = useState(cart.city || '');
	const [country, setCountry] = useState('NL');
	const [friend, setFriend] = useState(cart.friend || '');
	const { t } = useTranslation();
	if (!cart.lines.length) {
		return null;
	}
	const cartLines = [];
	let maxEta = 0;
	const totalPrice = cart.lines.reduce((acc, line) => {
		cartLines.push(<CartItem key={line.item.sku} {...line} setQuantity={amount => setCartItem(line.item, amount)} />);
		if(line.item.eta) {
			maxEta = Math.max(maxEta, line.item.eta);
		}
		return acc + line.item.price * line.quantity * (1 - (line.item.discount ?? 0));
	}, 0);
	const canSubmit = Boolean(name.trim() && email.trim() && phone.trim() && (!friends || address && postalCode && city && country));
	const deliveryDates = friends && getDeliveryDates(minDelivery, maxDelivery, maxEta, additionalWeeks);
	return <div className='cart'>
		{cartLines}
		<form className='cart-totals' onSubmit={async e => {
			e.preventDefault();
			if(canSubmit) {
				setSubmitting(true);
				try {
					await createOrder({ name, email, phone, address, postalCode, city, country, friend }, deliveryDate);
				} catch(err) {
					const message = err.response?.data?.message || err.message;
					window.alert(message);
				}
				setSubmitting(false);
			}
		}}>
			<span className='h4 mt-4 mb-4'>{t('orderDetails')}</span>
			<Input type="text" variant='filled' fullWidth required placeholder={t('name')} onChange={e => setName(e.target.value)} value={name} />
			<Input type="email" variant='filled' fullWidth required placeholder={t('email')} onChange={e => setEmail(e.target.value)} value={email} />
			<Input type="tel" variant='filled' fullWidth required placeholder={t('phone')} onChange={e => setPhone(e.target.value)} value={phone} />
			{friends && <>
				<Input type="text" variant="filled" fullWidth required placeholder={t('friend')} onChange={e => setFriend(e.target.value)} value={friend} />
				<Input type="text" variant="filled" fullWidth required placeholder={t('address')} onChange={e => setAddress(e.target.value)} value={address} />
				<Input type="text" variant="filled" fullWidth required placeholder={t('postalCode')} onChange={e => setPostalCode(e.target.value)} value={postalCode} />
				<Input type="text" variant="filled" fullWidth required placeholder={t('city')} onChange={e => setCity(e.target.value)} value={city} />
				<NeutralSelect variant="filled" fullWidth placeholder={t('country')} value={country} onChange={e => setCountry(e.target.value)} required>
					<MenuItem value="NL">{t('netherlands')}</MenuItem>
				</NeutralSelect>
				{maxEta > 0 && <p>
					{t('firstEta', { week: deliveryDates[0] ? deliveryDates[0].format(t('firstEtaFormat')) : t('firstEtaFallback') })}
				</p>}
				<NeutralSelect variant="filled" fullWidth placeholder={t('deliveryDate')} required value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}>
					<MenuItem value="" disabled>{t('deliveryDate')}</MenuItem>
					{deliveryDates.map(date => {
						const value = date.toISOString();
						return <MenuItem value={value} key={value}>{date.format(t('deliveryFormat'))}</MenuItem>;
					})}
				</NeutralSelect>
			</>}
			<hr align='right' />
			<span className='w-100 d-flex justify-content-end'>
				{t('total')} <Currency
					quantity={totalPrice}
					currency='EUR'
				/>
			</span>
			<motion.button
				type="submit"
				className='create-order-button'
				whileHover={{ scale: 1.1 }}
				whileTap={{ scale: 0.9 }}
				disabled={submitting || !canSubmit}
			>
				{t(friends ? 'order' : 'placeWishlist')} <CheckIcon />
			</motion.button>
		</form>
	</div>;
}
